import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export type ExportFormat = "pdf" | "png";
export type ExtraFooterText = string | string[];

const CHART_FRAME_HEIGHT = 380;

const HEADER_TITLE = "Analiza wyników";
const DISCLAIMER_TEXT =
  "Niniejsza analiza ma charakter informacyjny i nie stanowi diagnozy medycznej. " +
  "W celu pełnej oceny stanu słuchu zaleca się konsultację ze naszym specjalistą ze Świat Słuchu Dąbrowska & Kowalczyk.";

/* -------------------------------------------------------------------------- */
/* Stopka doklejana pod wykresem                                              */
/* -------------------------------------------------------------------------- */

export function appendTextToCanvas(
  originalCanvas: HTMLCanvasElement,
  extraFooterText: ExtraFooterText
): HTMLCanvasElement {
  const padding = 30;
  const contentWidth = originalCanvas.width - padding * 2;

  const tempCanvas = document.createElement("canvas");
  const ctxTemp = tempCanvas.getContext("2d")!;

  // 1. Podział disclaimera na linie
  ctxTemp.font = "500 18px sans-serif";
  const disclaimerLines: string[] = [];
  const maxBoxTextWidth = contentWidth - 40; // 20px paddingu wewnątrz baneru

  const disclaimerWords = DISCLAIMER_TEXT.split(" ");
  let currentDisclaimerLine = disclaimerWords[0] || "";

  for (let i = 1; i < disclaimerWords.length; i++) {
    const testLine = currentDisclaimerLine + " " + disclaimerWords[i];
    if (ctxTemp.measureText(testLine).width < maxBoxTextWidth) {
      currentDisclaimerLine = testLine;
    } else {
      disclaimerLines.push(currentDisclaimerLine);
      currentDisclaimerLine = disclaimerWords[i];
    }
  }
  disclaimerLines.push(currentDisclaimerLine);

  const boxLineHeight = 20;
  const boxPaddingY = 16;
  const disclaimerBoxHeight = disclaimerLines.length * boxLineHeight + boxPaddingY * 2;

  // 2. Punkty (tablica lub pojedynczy string z "\n")
  const rawPoints: string[] = (
    typeof extraFooterText === "string" ? [extraFooterText] : extraFooterText ?? []
  )
    .flatMap((p) => p.split("\n"))
    .map((p) => p.trim())
    .filter(Boolean);

  ctxTemp.font = "18px sans-serif";
  const preparedPoints: { lines: string[]; indent: number }[] = [];
  const pointLineHeight = 22;

  rawPoints.forEach((pt) => {
    const numberMatch = pt.match(/^(\d+\.\s*)/);
    const indentWidth = numberMatch ? ctxTemp.measureText(numberMatch[1]).width : 0;
    const maxTextWidth = contentWidth - indentWidth;

    const words = pt.split(" ");
    let curLine = words[0] || "";
    const lines: string[] = [];

    for (let i = 1; i < words.length; i++) {
      const test = curLine + " " + words[i];
      const limit = lines.length === 0 ? contentWidth : maxTextWidth;

      if (ctxTemp.measureText(test).width < limit) {
        curLine = test;
      } else {
        lines.push(curLine);
        curLine = words[i];
      }
    }
    lines.push(curLine);
    preparedPoints.push({ lines, indent: indentWidth });
  });

  let pointsTotalHeight = 0;
  preparedPoints.forEach((item) => {
    pointsTotalHeight += item.lines.length * pointLineHeight + 10;
  });

  const headerHeight = 35;
  const extraHeight =
    padding + headerHeight + disclaimerBoxHeight + 20 + pointsTotalHeight + padding;

  /* ---------------------------------- rysowanie --------------------------- */

  const finalCanvas = document.createElement("canvas");
  finalCanvas.width = originalCanvas.width;
  finalCanvas.height = originalCanvas.height + extraHeight;

  const ctx = finalCanvas.getContext("2d")!;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
  ctx.drawImage(originalCanvas, 0, 0);

  let currentY = originalCanvas.height + padding;

  ctx.font = "bold 24px sans-serif";
  ctx.fillStyle = "#000000";
  ctx.textBaseline = "top";
  ctx.fillText(HEADER_TITLE, padding, currentY);
  currentY += headerHeight;

  const boxX = padding;
  const boxY = currentY;

  ctx.fillStyle = "#3b7ddd";
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, contentWidth, disclaimerBoxHeight, 12);
  ctx.fill();

  ctx.font = "500 18px sans-serif";
  ctx.fillStyle = "#ffffff";
  let boxTextY = boxY + boxPaddingY;

  disclaimerLines.forEach((line) => {
    ctx.fillText(line, boxX + 20, boxTextY);
    boxTextY += boxLineHeight;
  });

  currentY += disclaimerBoxHeight + 20;

  if (preparedPoints.length > 0) {
    ctx.font = "18px sans-serif";
    ctx.fillStyle = "#212529";

    preparedPoints.forEach((item) => {
      item.lines.forEach((line, lineIdx) => {
        const xPos = lineIdx === 0 ? padding : padding + item.indent;
        ctx.fillText(line, xPos, currentY);
        currentY += pointLineHeight;
      });
      currentY += 10;
    });
  }

  return finalCanvas;
}

/* -------------------------------------------------------------------------- */
/* Zrzut panelu wyników                                                       */
/* -------------------------------------------------------------------------- */

export interface CaptureResultPanelOptions {
  container: HTMLElement | null;
  panelSelector: string;
  actionsClassName: string;
  /** Jeśli podane, stopka zostanie doklejona do zwróconego canvasa. */
  extraFooterText?: ExtraFooterText;
}

export async function captureResultPanel({
  container,
  panelSelector,
  actionsClassName,
  extraFooterText
}: CaptureResultPanelOptions): Promise<HTMLCanvasElement> {
  if (container === null) {
    throw new Error("Brak wykresu do eksportu.");
  }

  const resultPanelElement =
    container.firstElementChild instanceof HTMLElement
      ? container.firstElementChild
      : container;

  const exportWidth = Math.max(
    resultPanelElement.scrollWidth,
    resultPanelElement.clientWidth
  );

  const exportHeight = Math.max(
    resultPanelElement.scrollHeight,
    resultPanelElement.clientHeight
  );

  const canvas = await html2canvas(resultPanelElement, {
    backgroundColor: "#ffffff",
    scale: Math.min(window.devicePixelRatio || 1, 2),
    useCORS: true,
    width: exportWidth,
    height: exportHeight,
    windowWidth: exportWidth,
    windowHeight: exportHeight,
    onclone: (clonedDocument) => {
      const clonedPanel = clonedDocument.querySelector(panelSelector) as HTMLElement | null;
      const clonedSurfaces = clonedDocument.querySelectorAll(".recharts-surface");

      if (clonedPanel !== null) {
        clonedPanel.style.width = `${exportWidth}px`;
        clonedPanel.style.overflow = "visible";
        clonedPanel.style.height = "auto";
        clonedPanel.style.maxHeight = "none";

        clonedPanel.querySelectorAll(".result-chart__frame").forEach((frame) => {
          if (frame instanceof HTMLElement) {
            frame.style.overflow = "visible";
            frame.style.height = `${CHART_FRAME_HEIGHT}px`;
            frame.style.width = "100%";
          }
        });
      }

      // Remove the decorative CSS translateX so it doesn't clip on export.
      clonedSurfaces.forEach((surface) => {
        if (surface instanceof SVGElement) {
          surface.style.transform = "none";
        }
      });
    },
    ignoreElements: (element) => element.classList.contains(actionsClassName)
  });

  return extraFooterText ? appendTextToCanvas(canvas, extraFooterText) : canvas;
}

/* -------------------------------------------------------------------------- */
/* Generowanie plików PNG / PDF                                               */
/* -------------------------------------------------------------------------- */

export interface ResultFiles {
  canvas: HTMLCanvasElement;
  pngDataUrl: string;
  pngBase64: string;
  pdf: jsPDF;
  pdfBase64: string;
}

export function createPdfFromCanvas(
  canvas: HTMLCanvasElement,
  pngDataUrl = canvas.toDataURL("image/png")
): jsPDF {
  const pdf = new jsPDF({
    orientation: canvas.width >= canvas.height ? "landscape" : "portrait",
    unit: "px",
    format: [canvas.width, canvas.height]
  });

  pdf.addImage(pngDataUrl, "PNG", 0, 0, canvas.width, canvas.height);
  return pdf;
}

export function createResultFiles(canvas: HTMLCanvasElement): ResultFiles {
  const pngDataUrl = canvas.toDataURL("image/png");
  const pdf = createPdfFromCanvas(canvas, pngDataUrl);

  return {
    canvas,
    pngDataUrl,
    pngBase64: pngDataUrl.split(",")[1],
    pdf,
    pdfBase64: pdf.output("datauristring").split(",")[1]
  };
}

export function downloadResultFile(
  files: ResultFiles,
  format: ExportFormat,
  fileBaseName: string
): void {
  if (format === "png") {
    const link = document.createElement("a");
    link.href = files.pngDataUrl;
    link.download = `${fileBaseName}.png`;
    link.click();
    return;
  }

  files.pdf.save(`${fileBaseName}.pdf`);
}